import dayjs from "dayjs"

import { INCOGNITO_MASK } from "../../../Components/Private"
import Images from "../../../Images"
import { isIncognitoActive } from "../../../Utils/incognito"
import { serviceImgTooltip } from "../../../Utils/utils"

// How baseOptions' time axis prints the hovered day; a date set beside it must match.
export const axisTooltipDateFormat = "MMM DD, YYYY"

// Shared "current period | comparison period" card: one dark tooltip box (styling comes
// from the caller's own tooltipEl), two flex columns side by side with a divider between
// them when a comparison column is supplied, or just the one column when it isn't. This is
// a different shape from kpiChartOptions's stacked "Total / Prev. total" sections - there
// the comparison is a different day shown below; here it's the same day/bar in a second
// period, so it reads better next to its own current-period column than underneath it.
// Each row is { icon?, label, value } - icon is a full <img>/<svg> string (or omitted),
// same convention as kpiChartOptions's network/card rows, so a flag or logo sits inline
// with the label instead of nested inside it.
// `wrapLabels`: rows normally keep their label on one line (see chartsOptions.js's
// earlier nowrap fix for network names like "Ethoca" wrapping mid-word in a narrow
// column). Descriptor names can run much longer than a network name, so the descriptor
// tooltip opts into letting its labels wrap instead of overflowing past the card.
export const buildComparisonColumnsHtml = (
  columns,
  { wrapLabels = false, stacked = false } = {}
) => {
  const headerStyle = "font-weight:500; color:#7A7A83; padding-bottom:2px; white-space:nowrap;"
  const totalStyle = "font-weight:500; color:white; padding:6px 0 4px; white-space:nowrap;"
  const rowStyle = `display:flex; align-items:${wrapLabels ? "flex-start" : "center"}; gap:8px; padding:4px 0;`
  const rowLabelStyle = wrapLabels
    ? "flex:1; min-width:72px; white-space:normal; word-break:break-word; overflow-wrap:anywhere;"
    : "white-space:nowrap;"
  const rowValueStyle = "margin-left:auto; padding-left:16px; font-weight:500; white-space:nowrap;"
  return (
    `<div style="display:flex; ${stacked ? "flex-direction:column;" : ""}">` +
    columns
      .map((col, i) => {
        const rows = col.rows
          .map(
            (row) =>
              `<div style="${rowStyle}">${row.icon || ""}` +
              `<span style="${rowLabelStyle}">${row.label}</span><span style="${rowValueStyle}">${row.value}</span></div>`
          )
          .join("")
        const dividerStyle =
          i > 0
            ? stacked
              ? "margin-top:8px; padding-top:8px; border-top:1px solid rgba(255,255,255,0.12);"
              : "margin-left:16px; padding-left:16px; border-left:1px solid rgba(255,255,255,0.12);"
            : ""
        return (
          `<div style="flex:1 1 auto; min-width:0; ${dividerStyle}">` +
          `<div style="${headerStyle}">${col.header}</div>` +
          (col.total ? `<div style="${totalStyle}">${col.total}</div>` : "") +
          rows +
          "</div>"
        )
      })
      .join("") +
    "</div>"
  )
}

// The platform's chart tooltip: a dark card rendered into #chartjs-tooltip and floated
// above the caret. Extracted so every chart on this page shares one tooltip instead of
// some of them falling back to chart.js's built-in box, which looks nothing like it.
//
// The `title` callback supplies the lines: row 0 is the muted header (the date), row 1 is
// the headline value, and any further rows are detail. `iconForRow` decides whether a
// detail row carries a network logo, so a single-metric chart can reuse this untouched.
export function externalDarkTooltip({ theme, iconForRow }) {
  return function (context) {
    const dataPoints = context.tooltip.dataPoints?.[0]

    // Tooltip Element
    let tooltipEl = document.getElementById("chartjs-tooltip")

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement("div")
      tooltipEl.id = "chartjs-tooltip"
      let styleTable = "width: 100%;"
      tooltipEl.innerHTML = '<table style="' + styleTable + '" ></table>'
      // need clear charts custom tooltip element from body of page (because it can not remove on other pages automatically)
      //this logic see in initComponent
      document.body.appendChild(tooltipEl)
    }
    // Hide if no tooltip
    const tooltipModel = context.tooltip
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0
      if (tooltipEl._hideOnTouchOutside) {
        document.removeEventListener("touchstart", tooltipEl._hideOnTouchOutside)
        tooltipEl._hideOnTouchOutside = null
      }
      return
    }

    // Set caret Position
    tooltipEl.classList.remove("above", "below", "no-transform")
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign)
    } else {
      tooltipEl.classList.add("no-transform")
    }

    // A __RAW__ comparison card (kpiChartOptions's two flex columns) needs more room than
    // the plain single-column rows this tooltip otherwise renders - minWidth is only a
    // floor, the flex columns still size to their own content past it.
    const isComparisonCard = (tooltipModel.title || []).some(
      (value) => typeof value === "string" && value.startsWith("__RAW__")
    )
    const width = isComparisonCard ? 240 : 154

    tooltipEl.style.zIndex = "1010"
    tooltipEl.style.backgroundColor = "#27272A"
    tooltipEl.style.borderRadius = "12px"
    tooltipEl.style.padding = "12px 12px"
    tooltipEl.style.color = "white"
    tooltipEl.style.minWidth = width + "px"
    // Set Text
    if (tooltipModel.body) {
      const titleLines = tooltipModel.title || []

      let innerHtml = "<thead>"
      let styleTitle = "padding-bottom: 0px; text-align: start; font-weight: 500; color: #7A7A83"
      let styleTitle2 =
        "padding: 0px; padding-top: 12px; padding-bottom: 12px; font-weight: 400; text-align: start"
      let styleTitle3 = "padding: 0px; padding-top: 4px; font-weight: 400; text-align: start;"

      titleLines.forEach(function (value, i) {
        // A caller that needs a layout the fixed i===0/1/2+ rows can't express (multiple
        // header/headline sections, e.g. kpiChartOptions's current + comparison blocks)
        // supplies its own fully-styled HTML per line instead, prefixed so this loop knows
        // to drop it in untouched rather than wrapping it in the fixed row styles below.
        if (typeof value === "string" && value.startsWith("__RAW__")) {
          const cleaned = value.replace("__RAW__", "")
          innerHtml += '<tr><th style="padding:0;text-align:start;">' + cleaned + "</th></tr>"
          return
        }
        if (i === 0) {
          innerHtml += '<tr><th style="' + styleTitle + '" >' + value + "</th></tr>"
          return
        }
        if (i === 1) {
          innerHtml += '<tr><th style="' + styleTitle2 + '" >' + value + "</th></tr>"
          return
        }
        const icon = iconForRow ? iconForRow(i) : null
        const logo = icon
          ? '<img src="' +
            Images[serviceImgTooltip[theme][icon]] +
            '" width="20" height="auto" style="margin-right: 8px;" alt="' +
            icon +
            ' logo">'
          : ""
        innerHtml += '<tr><th style="' + styleTitle3 + '">' + logo + value + "</th></tr>"
      })
      innerHtml += "</thead><tbody>"

      let tableRoot = tooltipEl.querySelector("table")
      tableRoot.innerHTML = innerHtml
    }

    const position = context.chart.canvas.getBoundingClientRect()
    // const bodyFont = Chart.helpers.toFont(tooltipModel.options.bodyFont);

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1
    tooltipEl.style.position = "absolute"

    let tooltipWidth = tooltipEl?.offsetWidth
    let deltaLeft = tooltipWidth / 2
    if (dataPoints?.chart.width - tooltipModel.caretX <= tooltipWidth / 2) {
      deltaLeft = tooltipWidth * 0.75
    } else if (tooltipModel.caretX < tooltipWidth / 2) {
      deltaLeft = tooltipWidth * 0.25
    }

    const tooltipHeight2 = tooltipEl?.offsetHeight || 160
    let left2 = position.left - deltaLeft + window.scrollX + tooltipModel.caretX
    let top2 = position.top - (tooltipHeight2 + 20) + window.scrollY + tooltipModel.caretY
    left2 = Math.max(
      window.scrollX,
      Math.min(left2, window.scrollX + window.innerWidth - tooltipEl.offsetWidth)
    )
    top2 = Math.max(
      window.scrollY,
      Math.min(top2, window.scrollY + window.innerHeight - tooltipHeight2)
    )
    tooltipEl.style.left = left2 + "px"
    tooltipEl.style.top = top2 + "px"
    // tooltipEl.style.font = bodyFont.string;
    tooltipEl.style.padding = tooltipModel.padding + "px "
    tooltipEl.style.pointerEvents = "none"

    if (!tooltipEl._hideOnTouchOutside) {
      const canvas = context.chart.canvas
      tooltipEl._hideOnTouchOutside = (e) => {
        if (!canvas.contains(e.target)) {
          tooltipEl.style.opacity = 0
          document.removeEventListener("touchstart", tooltipEl._hideOnTouchOutside)
          tooltipEl._hideOnTouchOutside = null
        }
      }
      document.addEventListener("touchstart", tooltipEl._hideOnTouchOutside, { passive: true })
    }
  }
}

const KPI_NETWORK_ROWS = [
  { name: "Ethoca", service: "ethoca" },
  { name: "RDR", service: "rdr" },
  { name: "CDRN", service: "cdrn" }
]

// The per-network rows under a `kpiTooltip` total on Prevent analytics: `values` is the
// backend's {ethoca|rdr|cdrn: [...perBucket]} breakdown. Every network is listed even when it
// has no series, so the card keeps the same rows. Deflect has no networks: its rows are
// channels (DeflectAnalyticsPage/utils/chartOptions.js).
export const kpiNetworkRows = ({ theme, formatValue, values, dataIndex }) =>
  values
    ? KPI_NETWORK_ROWS.map(({ name, service }) => ({
        icon:
          '<img src="' +
          Images[serviceImgTooltip[theme][service]] +
          '" width="20" height="20" style="border-radius:50%;flex-shrink:0;" alt="' +
          service +
          ' logo">',
        label: name,
        value: isIncognitoActive() ? INCOGNITO_MASK : formatValue(values[service]?.[dataIndex] ?? 0)
      }))
    : []

// The KPI trend's hover card, shared by Prevent and Deflect analytics: the metric name once on
// top, then the hovered day and, while a comparison is drawn, the same index in the comparison
// window beside it. `rows` / `comparisonRows` add a per-dimension breakdown under each total.
// `totalLabel` heads each value: `Total` by default, `Rate` for a percentage, `Prev. …` beside it.
export const kpiTooltip = ({
  theme,
  label,
  formatValue,
  rows,
  comparisonRows,
  totalLabel = "Total"
}) => {
  const display = (value) => (isIncognitoActive() ? INCOGNITO_MASK : formatValue(value))

  const title = (context) => {
    const point = context?.[0]
    // The comparison dataset (index 1) is on the chart only when the backend sent a comparison
    // series - otherwise the second column is skipped rather than shown empty.
    const comparisonPoint = context?.find((entry) => entry.datasetIndex === 1)

    const columns = [
      {
        header: point?.label ?? "",
        total: `${totalLabel}: ${display(point?.parsed?.y)}`,
        rows: rows?.(point?.dataIndex) ?? []
      }
    ]

    if (comparisonPoint) {
      // The comparison series is aligned to `labels` by index, but its own calendar date (e.g.
      // the same day one month back) differs from the hovered day's, so it rides on the dataset.
      const rawComparisonDate =
        comparisonPoint.dataset?.comparisonLabels?.[comparisonPoint.dataIndex]
      const comparisonDate = rawComparisonDate
        ? dayjs(rawComparisonDate).format(axisTooltipDateFormat)
        : (comparisonPoint.dataset?.label ?? "")

      columns.push({
        header: comparisonDate,
        total: `Prev. ${totalLabel.toLowerCase()}: ${display(comparisonPoint.parsed?.y)}`,
        rows: comparisonRows?.(comparisonPoint.dataIndex) ?? []
      })
    }

    // The metric name is shared context for the whole card, not per-period, so it sits once
    // above the column(s) instead of repeating in each one.
    return [
      `__RAW__<div style="font-weight:500; color:#7A7A83; padding-bottom:8px;">${label ?? ""}</div>`,
      `__RAW__${buildComparisonColumnsHtml(columns)}`
    ]
  }

  return {
    enabled: false,
    callbacks: { title },
    // Every line is __RAW__ HTML, icons included - no per-index icon lookup needed here.
    external: externalDarkTooltip({ theme })
  }
}
